"use client";

import React from "react";

import { cn } from "./cn";

/**
 * Port of the reference's DataTable (src/components/ui/DataTable.tsx).
 * Verbatim except the `cn` import, which points at this package's own
 * ./cn instead of the reference's `@/lib/utils` — same twMerge(clsx(...))
 * combinator, not a domain-logic dependency.
 */

export interface ColumnDef<T> {
  key: string;
  header: React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
  width?: string | number;
  /** Custom cell renderer. When absent, the value at `row[key]` is used. */
  render?: (row: T) => React.ReactNode;
  /**
   * Optional click handler for the <td> element itself. Useful for columns
   * that must stop event propagation (e.g. checkbox and actions columns)
   * so that row-level `onRowClick` is not triggered.
   */
  onCellClick?: (e: React.MouseEvent<HTMLTableCellElement>, row: T) => void;
  /** Optional tooltip text for the <td> element derived from the row. */
  getCellTitle?: (row: T) => string | undefined;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /** Node rendered inside a full-width cell when rows is empty. */
  empty?: React.ReactNode;
  /** Apply `.table-striped` class. Defaults to true. */
  striped?: boolean;
  /** Apply `.table-sticky` class. Defaults to false. */
  stickyHeader?: boolean;
  className?: string;
  /** Accessible label forwarded to the <table> element. */
  "aria-label"?: string;
  /**
   * Mobile card renderer. When provided, the table is hidden below `md:` and
   * each row renders as a tappable card (content owned by the caller, tap
   * behavior/chrome owned here) — list pages inherit card mode with zero
   * page-level layout code. Omit it and the table renders at every width.
   */
  mobileCard?: (row: T) => React.ReactNode;
}

/**
 * Presentational table primitive. It owns no data fetching, no sorting, and no
 * filtering — callers pass already-sorted `rows` and column definitions. It
 * renders with the existing globals.css table utilities so visual output
 * matches the hand-rolled tables it replaces. Cell classes are merged with
 * `cn` (tailwind-merge) so a column's `className` overrides the default
 * padding rather than colliding with it.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  striped = true,
  stickyHeader = false,
  className,
  "aria-label": ariaLabel,
  mobileCard,
}: DataTableProps<T>) {
  const tableClassName = cn(
    "w-full text-sm",
    striped && "table-striped",
    stickyHeader && "table-sticky",
    onRowClick && "table-accent-hover",
    // With a mobile card renderer the table is desktop-only chrome.
    mobileCard && "hidden md:table",
    className,
  );

  const alignClass = (align: ColumnDef<T>["align"]) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  /* Mobile card list (<md). Rows become ≥44px tappable cards; the whole card
     is the tap target when onRowClick exists. */
  const cardList = mobileCard && (
    <ul className="flex flex-col gap-2.5 md:hidden" aria-label={ariaLabel}>
      {rows.length === 0 && empty != null && <li>{empty}</li>}
      {rows.map((row) => (
        <li key={rowKey(row)}>
          {onRowClick ? (
            <button
              type="button"
              onClick={() => onRowClick(row)}
              className="w-full min-h-11 text-left ds-card rounded-xl px-4 py-3.5 transition-colors active:bg-elevated"
            >
              {mobileCard(row)}
            </button>
          ) : (
            <div className="min-h-11 ds-card rounded-xl px-4 py-3.5">{mobileCard(row)}</div>
          )}
        </li>
      ))}
    </ul>
  );

  const table = (
    <table className={tableClassName} aria-label={ariaLabel}>
      <thead>
        <tr className="border-b border-edge bg-elevated">
          {columns.map((col) => (
            <th
              key={col.key}
              scope="col"
              className={cn("px-5 py-3", alignClass(col.align), col.className)}
              style={col.width != null ? { width: col.width } : undefined}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && empty != null && (
          <tr>
            <td colSpan={columns.length}>{empty}</td>
          </tr>
        )}
        {rows.map((row) => (
          <tr
            key={rowKey(row)}
            className={cn(
              "border-b border-edge last:border-0 transition-colors",
              onRowClick && "cursor-pointer hover:bg-elevated",
            )}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {columns.map((col) => (
              <td
                key={col.key}
                className={cn(
                  "px-5 py-3",
                  col.align === "right" ? "text-right tabular-nums" : col.align === "center" ? "text-center" : "",
                  col.className,
                )}
                style={col.width != null ? { width: col.width } : undefined}
                onClick={col.onCellClick ? (e) => col.onCellClick!(e, row) : undefined}
                title={col.getCellTitle ? col.getCellTitle(row) : undefined}
              >
                {col.render ? col.render(row) : ((row as Record<string, unknown>)[col.key] as React.ReactNode)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (!mobileCard) return table;
  return (
    <>
      {cardList}
      {table}
    </>
  );
}
